/**
 * What the shared signing dialog needs to show a DocuSign recipient view: the one-time `url`, and for the in-app
 * focused view the DocuSign.js `scriptUrl` and `integrationKey`. `useEmbedded: false` (the server kill switch) means a
 * full-page redirect to `url` instead.
 */
export interface DocuSignSigningSession {
  url: string;
  useEmbedded: boolean;
  integrationKey: string;
  scriptUrl: string;
}

/** Who signs in the dialog; drives the default title. */
export type DocuSignSignerRole = 'hr' | 'employee';

/**
 * A second way to start, offered as a button when the first start fails with a matching error, e.g. an HR admin taking
 * over another HR user's session (WI 34398). `helpAnchor` names the button for the Ask Ryn wren.
 */
export interface DocuSignSigningRetryAction {
  label: string;
  appliesTo: (error: unknown) => boolean;
  sessionProvider: () => Promise<DocuSignSigningSession | null>;
  helpAnchor?: string;
}

/** How a signing ceremony ended. */
export type DocuSignSigningOutcome = 'completed' | 'declined' | 'cancelled';

/** What the dialog is waiting on while its spinner shows. */
export type DocuSignSigningStage = 'preparing' | 'opening' | 'recording';

/**
 * Map a DocuSign.js focused-view `sessionEnd` event to an outcome. Only `signing_complete` is a completion and only
 * `decline` a decline; every other end (`cancel`, `session_timeout`, `ttl_expired`, `exception`, `viewing_complete`,
 * unknown or missing) is a cancel, so an ambiguous end never reports success. The value has lived under
 * `sessionEndType` and `type` across SDK versions, and the event is sometimes the bare string.
 */
export function outcomeFromSessionEnd(event: unknown): DocuSignSigningOutcome {
  const value =
    typeof event === 'string'
      ? event
      : ((event as { sessionEndType?: string; type?: string } | null)
          ?.sessionEndType ??
        (event as { type?: string } | null)?.type ??
        '');
  switch (value) {
    case 'signing_complete':
      return 'completed';
    case 'decline':
      return 'declined';
    default:
      return 'cancelled';
  }
}

/** Spinner headline for each stage. */
export function signingStageMessage(stage: DocuSignSigningStage): string {
  switch (stage) {
    case 'preparing':
      return 'Preparing the documents in DocuSign…';
    case 'opening':
      return 'Opening the DocuSign signing view…';
    case 'recording':
      return 'Recording the signature…';
  }
}

/** Usual wait, in seconds, from opening the dialog to the DocuSign view, shown so the signer knows the wait is normal. */
export const USUAL_LOAD_SECONDS = { min: 15, max: 45 };

/** Past this many seconds the hint says the wait is longer than usual. */
export const SLOW_LOAD_SECONDS = 60;

/**
 * The line under the spinner headline: the usual wait plus the time so far, and once the wait passes
 * {@link SLOW_LOAD_SECONDS}, a note that it is slower than usual so the signer keeps the dialog open instead of retrying.
 */
export function loadingHintFor(elapsedSeconds: number): string {
  const elapsed = `${elapsedSeconds} s so far`;
  if (elapsedSeconds >= SLOW_LOAD_SECONDS) {
    return `This is taking longer than usual (${elapsed}). DocuSign is still working, please keep this window open.`;
  }
  return `This usually takes ${USUAL_LOAD_SECONDS.min}–${USUAL_LOAD_SECONDS.max} seconds (${elapsed}).`;
}

/** The default dialog title for a signer role. */
export function signingTitleFor(role: DocuSignSignerRole): string {
  return role === 'hr' ? 'HR signing' : 'Sign your documents';
}

/** The `detail` (or `message`) of a failed API call, else a fallback, for showing in the dialog. */
export function signingErrorMessage(error: unknown): string {
  const body = (error as { error?: unknown } | null)?.error;
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (body && typeof body === 'object') {
    const detail =
      (body as { detail?: unknown }).detail ??
      (body as { message?: unknown }).message;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Something went wrong starting DocuSign signing.';
}
